/**
 * THE app font stack behind the design toggle, and the single source of it.
 *
 * Two consumers read this constant, because the app paints text through two
 * unrelated systems and neither one can reach the other's rules:
 *
 *   1. `src/styles/AppFont.tsx` — one `html body` rule, so every plain element
 *      and every styled-component inherits it.
 *   2. `src/App/AntdI18n.tsx` — antd's `token.fontFamily`. antd v5 is NOT
 *      inheritance-based here: `resetComponent()` (`antd/es/style/index.js`)
 *      writes `font-family: <token.fontFamily>` into the generated CSS of
 *      nearly every component — Button, Table, Form, Select, Modal — and
 *      cssinjs injects those rules UNLAYERED, so a `body` rule cannot outrank
 *      them. Change only the body rule and the app splits: prose in Geist,
 *      every antd control still on the system stack.
 *
 * Neither dispatch point reaches antd v5's static `message.*`/`notification.*`,
 * which render outside `ConfigProvider` by default; `src/ui/toast/index.ts`
 * closes that gap on its own and owns the reasoning — read its header comment
 * rather than a copy here.
 *
 * A third consumer, `--font-sans` in `src/styles/tailwind.css`, carries the
 * same stack verbatim for `font-sans` utilities on ported surfaces;
 * `__tests__/app-font.test.tsx` asserts the two texts agree.
 *
 * The tail is the system stack Geist prepends to, kept character-for-character
 * from `src/App/global.styles.ts` (the `body` rule, which is also antd's own
 * default `token.fontFamily`). So a blocked, failed or still-loading woff2
 * lands exactly where the app renders today rather than on a UA serif — and
 * with the toggle off nothing reads this constant at all.
 */
export const GEIST_FONT_FAMILY =
  "'Geist Variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'"
